"use client";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { DrupalNode } from "next-drupal";
import Image from "next/image";
import Link from "next/link";
import { MdChevronRight } from "react-icons/md";
import { config } from "@/lib/config";
import { resolveLink } from "@/utils";

interface SliderProps {
  sliderData: DrupalNode[];
  isHomePage?: boolean;
}

const PartnerCard = ({
  partnerCard,
  isHomePage,
}: {
  partnerCard: DrupalNode;
  isHomePage?: boolean;
}) => {
  const rawLink =
    partnerCard?.field_mission_card_link ||
    partnerCard?.field_button_link?.uri;
  const href = resolveLink(rawLink);

  return (
    <div className="flex-shrink-0 px-[15px] py-5 mobileMax:px-2">
      <Link
        href={href}
        target={href === "#" ? undefined : "_blank"}
        onClick={(e) => {
          if (href === "#") {
            e.preventDefault();
          }
        }}
        className="group relative w-full p-3 mobileMax:px-2 bg-white rounded-[20px] overflow-hidden 
        shadow-[0_2px_5px_rgba(0,0,0,0.15)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.15)]
        mobileMax:p-1 min-w-[280px] mobileMax:min-w-[180px] min-h-[150px] mobileMax:min-h-[120px] mobileMax:h-[120px] 
        flex items-center justify-center duration-300 isolate"
      >
        {(partnerCard?.field_mission_card_image?.uri?.url ||
          partnerCard?.field_icon?.uri?.url) && (
          <Image
            src={`${config.apiBase}${
              partnerCard?.field_mission_card_image?.uri?.url ||
              partnerCard?.field_icon?.uri?.url
            }`}
            alt={partnerCard?.field_mission_card_image?.filename || "icon"}
            width={200}
            height={200}
            className={`mobileMax:max-w-[130px] max-h-[110px] mobileMax:max-h-[80px] ${
              isHomePage ? "object-contain" : ""
            }`}
          />
        )}

        {href !== "#" && (
          <div className="absolute bottom-0 left-0 w-full translate-y-full group-hover:translate-y-0 transition-transform duration-500 will-change-transform">
            <div className="w-full h-[30px] bg-[linear-gradient(to_right,#8EBCE5,#11375C)] flex flex-col items-center text-white font-medium text-sm overflow-hidden">
              <div className="w-full rounded-b-[15px] h-[15px] bg-white" />
              <p className="inline-flex mt-[2px] mb-[2px]">
                {partnerCard?.field_button_link?.title || "Learn More"}
                <MdChevronRight className="w-[18px] h-[18px] ml-0.5 mt-[2px]" />
              </p>
            </div>
          </div>
        )}
      </Link>
    </div>
  );
};

const InfiniteSlider = ({
  items,
  reverse = false,
  isHomePage,
}: {
  items: DrupalNode[];
  reverse?: boolean;
  isHomePage?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [animationWidth, setAnimationWidth] = useState(0);

  // Repeat items only if we need to animate to ensure smooth infinite scroll
  const fullSet = useMemo(() => {
    if (!items || items.length === 0) return [];
    if (!shouldAnimate) return items;

    let result = [...items];
    while (result.length > 0 && result.length < 20) {
      result = [...result, ...items];
    }
    return result;
  }, [items, shouldAnimate]);

  const renderItems = useMemo(
    () => (shouldAnimate ? [...fullSet, ...fullSet] : fullSet),
    [fullSet, shouldAnimate],
  );

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current && wrapperRef.current) {
        const wrapperWidth = wrapperRef.current.clientWidth;
        // To measure correctly, we temporarily use a non-repeated set if not already determined
        // But since we use renderItems which depends on shouldAnimate,
        // we can measure the total scrollWidth and divide by 2 if shouldAnimate is true.
        const currentTotalWidth = containerRef.current.scrollWidth;
        const singleSetWidth = shouldAnimate
          ? currentTotalWidth / 2
          : currentTotalWidth;

        if (singleSetWidth > wrapperWidth) {
          setShouldAnimate(true);
          setAnimationWidth(singleSetWidth);
        } else {
          setShouldAnimate(false);
          setAnimationWidth(0);
        }
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);

    // Initial load check
    const timeout = setTimeout(updateWidth, 500);

    return () => {
      window.removeEventListener("resize", updateWidth);
      clearTimeout(timeout);
    };
  }, [items, shouldAnimate]); // Re-run if items change or we toggle animation state

  if (!items || items.length === 0) return null;

  return (
    <div ref={wrapperRef} className="overflow-hidden relative w-full">
      <div
        ref={containerRef}
        className={`flex w-max items-center ${!shouldAnimate ? "justify-center !w-full" : ""}`}
        style={{
          animation:
            shouldAnimate && animationWidth > 0
              ? `${reverse ? "marqueeReverse" : "marquee"} ${animationWidth / 60}s linear infinite`
              : "none",
        }}
        onMouseEnter={(e) => {
          if (shouldAnimate) e.currentTarget.style.animationPlayState = "paused";
        }}
        onMouseLeave={(e) => {
          if (shouldAnimate)
            e.currentTarget.style.animationPlayState = "running";
        }}
      >
        {renderItems.map((item, i) => (
          <PartnerCard key={i} partnerCard={item} isHomePage={isHomePage} />
        ))}
      </div>

      <style jsx>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        @keyframes marqueeReverse {
          0% {
            transform: translateX(-50%);
          }
          100% {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

const PartnerSlider: React.FC<SliderProps> = ({
  sliderData,
  isHomePage = false,
}) => {
  const filteredData = useMemo(() => {
    return (sliderData || []).filter((item) => {
      const link = item?.field_mission_card_link || item?.field_button_link?.uri;
      return link !== undefined && link !== null && link !== "";
    });
  }, [sliderData]);

  const total = filteredData.length;
  const firstHalfCount = Math.ceil(total / 2);
  const firstHalf = useMemo(
    () => filteredData.slice(0, firstHalfCount),
    [filteredData, firstHalfCount],
  );
  const secondHalf = useMemo(
    () => filteredData.slice(firstHalfCount, total),
    [filteredData, firstHalfCount, total],
  );

  if (total === 0) return null;

  return (
    <div className="space-y-4 mobileMax:space-y-2">
      {total < 15 ? (
        <InfiniteSlider items={filteredData} isHomePage={isHomePage} />
      ) : (
        <>
          <InfiniteSlider items={firstHalf} isHomePage={isHomePage} />
          <InfiniteSlider items={secondHalf} reverse isHomePage={isHomePage} />
        </>
      )}
    </div>
  );
};

export default PartnerSlider;
